import { useState, useEffect, useCallback, useRef } from 'react';

export interface InfiniteScrollOptions<T> {
  /** Função que busca os dados da API */
  fetchFunction: (page: number, limit: number, filters?: any) => Promise<{
    success: boolean;
    data: T[] | { items: T[]; pagination?: { total: number } };
    message?: string;
  }>;
  /** Número de itens por página (padrão: 10) */
  itemsPerPage?: number;
  /** Filtros adicionais para aplicar na busca */
  filters?: any;
  /** Callback chamado quando ocorre um erro */
  onError?: (error: string) => void;
  /** Habilita carregamento automático ao rolar (padrão: false - usa botão "Ver Mais") */
  autoLoad?: boolean;
}

export interface InfiniteScrollResult<T> {
  /** Lista de itens a serem exibidos (limitada pelo displayedItems) */
  items: T[];
  /** Indica se está carregando dados */
  isLoading: boolean;
  /** Indica se tem mais itens para exibir */
  hasMore: boolean;
  /** Total de itens disponíveis */
  totalItems: number;
  /** Número de itens exibidos atualmente */
  displayedItems: number;
  /** Função para carregar mais itens (apenas incrementa a quantidade exibida) */
  loadMore: () => void;
  /** Função para resetar e recarregar desde o início */
  reset: () => void;
  /** Referência para o elemento sentinela (colocar no final da lista) */
  sentinelRef: (node: HTMLElement | null) => void;
  /** Função para carregar TODOS os dados do backend (útil para relatórios/impressão) */
  loadAll: () => Promise<void>;
}

/**
 * Hook customizado para implementar paginação no frontend
 * Busca TODOS os dados do backend de uma vez e faz a paginação apenas no frontend
 *
 * @example
 * ```tsx
 * const { items, isLoading, totalItems, displayedItems, loadMore } = useInfiniteScroll({
 *   fetchFunction: async (page, limit, filters) => {
 *     return await apiService.getVehicles({ page, limit, ...filters });
 *   },
 *   itemsPerPage: 10,
 *   filters: { status: 'ativo' }
 * });
 *
 * return (
 *   <div>
 *     <p>Exibindo {displayedItems} de {totalItems} resultados</p>
 *     {items.map(item => <ItemCard key={item.id} item={item} />)}
 *     <LoadMoreButton onClick={loadMore} hasMore={hasMore} />
 *   </div>
 * );
 * ```
 */
export function useInfiniteScroll<T>({
  fetchFunction,
  itemsPerPage = 10,
  filters = {},
  onError,
  autoLoad = false
}: InfiniteScrollOptions<T>): InfiniteScrollResult<T> {
  const [allItems, setAllItems] = useState<T[]>([]); // Itens já carregados do backend
  const [currentPage, setCurrentPage] = useState(1); // Página atual
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0); // Total real do backend
  const [hasMoreInBackend, setHasMoreInBackend] = useState(true); // Se tem mais no backend
  const observerRef = useRef<IntersectionObserver | null>(null);
  const prevFiltersRef = useRef<string>(''); // Ref para armazenar filtros anteriores
  const requestIdRef = useRef(0); // ID incremental para identificar e cancelar requisições antigas

  // Função para carregar uma página de dados do backend
  const loadData = useCallback(async (page: number) => {
    // Incrementa ID para identificar esta requisição de forma única
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);
    try {
      const response = await fetchFunction(page, itemsPerPage, filters);

      // Verifica se esta ainda é a requisição mais recente (evita race conditions com filtros)
      if (currentRequestId !== requestIdRef.current) {
        // Uma nova requisição foi iniciada (filtros mudaram), ignora este resultado
        return;
      }

      if (!response.success) {
        // Em caso de erro, limpa apenas se for a primeira página
        if (page === 1) {
          setAllItems([]);
          setTotalItems(0);
        }
        setHasMoreInBackend(false);
        if (onError) {
          onError(response.message || 'Erro ao carregar dados');
        }
        return;
      }

      let pageItems: T[] = [];
      let total = 0;

      // Suporta dois formatos de resposta da API
      if (Array.isArray(response.data)) {
        // Formato simples: response.data = [...]
        pageItems = response.data;
        total = response.data.length;
      } else if (response.data && 'items' in response.data) {
        // Formato com paginação: response.data = { items: [...], pagination: { total: X } }
        pageItems = response.data.items || [];
        total = response.data.pagination?.total || pageItems.length;
      }

      // Atualiza o estado
      setAllItems(prev => page === 1 ? pageItems : [...prev, ...pageItems]);
      setTotalItems(total);
      setCurrentPage(page);

      // Verifica se tem mais itens no backend
      const loadedSoFar = page === 1 ? pageItems.length : allItems.length + pageItems.length;
      setHasMoreInBackend(loadedSoFar < total && pageItems.length === itemsPerPage);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);

      // Só atualiza estado de erro se esta é a requisição mais recente
      if (currentRequestId === requestIdRef.current) {
        if (page === 1) {
          setAllItems([]);
          setTotalItems(0);
        }
        setHasMoreInBackend(false);
        if (onError) {
          onError('Erro ao carregar dados. Tente novamente.');
        }
      }
    } finally {
      // Só desliga loading se esta é a requisição mais recente
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetchFunction, filters, itemsPerPage, onError, allItems.length]);

  // Carrega mais itens (busca próxima página do backend)
  const loadMore = useCallback(() => {
    if (!isLoading && hasMoreInBackend) {
      loadData(currentPage + 1);
    }
  }, [isLoading, hasMoreInBackend, currentPage, loadData]);

  // Reset e recarrega desde o início
  const reset = useCallback(() => {
    setAllItems([]);
    setCurrentPage(1);
    setTotalItems(0);
    setHasMoreInBackend(true);
    loadData(1);
  }, [loadData]);

  // Carrega TODOS os dados do backend (útil para relatórios/impressão)
  const loadAll = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      let allLoadedItems: T[] = [];
      let currentPageNum = 1;
      let shouldContinue = true;

      // Busca primeira página para saber o total
      const firstPageResponse = await fetchFunction(1, itemsPerPage, filters);

      if (!firstPageResponse.success) {
        if (onError) {
          onError(firstPageResponse.message || 'Erro ao carregar todos os dados');
        }
        return;
      }

      let totalToLoad = 0;
      let firstPageItems: T[] = [];

      if (Array.isArray(firstPageResponse.data)) {
        firstPageItems = firstPageResponse.data;
        totalToLoad = firstPageResponse.data.length;
      } else if (firstPageResponse.data && 'items' in firstPageResponse.data) {
        firstPageItems = firstPageResponse.data.items || [];
        totalToLoad = firstPageResponse.data.pagination?.total || firstPageItems.length;
      }

      allLoadedItems = [...firstPageItems];

      // Se já carregou tudo na primeira página, não precisa continuar
      if (allLoadedItems.length >= totalToLoad) {
        setAllItems(allLoadedItems);
        setTotalItems(totalToLoad);
        setCurrentPage(1);
        setHasMoreInBackend(false);
        return;
      }

      // Continua carregando as outras páginas
      currentPageNum = 2;

      while (shouldContinue && allLoadedItems.length < totalToLoad) {
        const pageResponse = await fetchFunction(currentPageNum, itemsPerPage, filters);

        if (!pageResponse.success) {
          break;
        }

        let pageItems: T[] = [];

        if (Array.isArray(pageResponse.data)) {
          pageItems = pageResponse.data;
        } else if (pageResponse.data && 'items' in pageResponse.data) {
          pageItems = pageResponse.data.items || [];
        }

        if (pageItems.length === 0) {
          break;
        }

        allLoadedItems = [...allLoadedItems, ...pageItems];

        // Para quando carregar menos que o limite (última página)
        if (pageItems.length < itemsPerPage) {
          shouldContinue = false;
        }

        currentPageNum++;
      }

      // Atualiza o estado com TODOS os itens
      setAllItems(allLoadedItems);
      setTotalItems(totalToLoad);
      setCurrentPage(currentPageNum - 1);
      setHasMoreInBackend(false); // Já carregou tudo

    } catch (error) {
      console.error('Erro ao carregar todos os dados:', error);
      if (onError) {
        onError('Erro ao carregar todos os dados. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchFunction, filters, itemsPerPage, onError]);

  // Intersection Observer para detectar quando o usuário rolou até o final
  // Só ativa se autoLoad estiver habilitado
  const sentinelRef = useCallback((node: HTMLElement | null) => {
    if (!autoLoad || isLoading) return;

    // Desconecta o observer anterior
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Cria novo observer
    if (node) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMoreInBackend && !isLoading) {
            loadMore();
          }
        },
        {
          root: null,
          rootMargin: '100px', // Começa a carregar 100px antes de chegar no final
          threshold: 0.1
        }
      );

      observerRef.current.observe(node);
    }
  }, [autoLoad, isLoading, hasMoreInBackend, loadMore]);

  // Carrega dados iniciais e quando os filtros REALMENTE mudam (comparação profunda)
  useEffect(() => {
    const currentFilters = JSON.stringify(filters);

    // Só recarrega se os filtros realmente mudaram (comparação de valores, não referência)
    if (currentFilters !== prevFiltersRef.current) {
      prevFiltersRef.current = currentFilters;
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, reset]); // Monitora filters, mas só reseta se valores mudaram

  // Cleanup do observer
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    items: allItems,
    isLoading,
    hasMore: hasMoreInBackend,
    totalItems,
    displayedItems: allItems.length,
    loadMore,
    reset,
    sentinelRef,
    loadAll
  };
}
